import api from "@/api";
import i18n from "@/i18n";

import utilities from "@/helpers/utilities";
import Demand from "@/helpers/entities/Demand";

const state = {
    demands: [],
    demandsGrid: [],
    evaluatedDemandsGrid: [],
    demandComments: {},
    demand: {}
};

const mutations = {
    setDemands(state, demands) {
        state.demands = demands;
    },
    setDemandsGrid(state, demands) {
        state.demandsGrid = demands;
    },
    setEvaluatedDemandsGrid(state, demands) {
        state.evaluatedDemandsGrid = demands;
    },
    setDemandComments(state, demand) {
        state.demandComments = demand;
    },
    setDemand(state, demand) {
        state.demand = demand;
    },
    appendDemand(state, demand) {
        state.demands.splice(0, 0, demand);
    },
    updateDemand(state, demand) {
        const index = state.demands.map(item => item.id).indexOf(demand.id);
        state.demands.splice(index, 1, demand);
    }
};

const actions = {
    // CRUD
    // consulta a demanda selecionada no Grid tela Demadas
    // (tela src\components\demands\Edit.vue fetchDemand).
    async fetchDemand({ commit, dispatch }, id) {
        const response = await api.demands.fetch(id);
        commit("setDemand", response.data);
        dispatch("syncDemand", response.data);
    },
    async loadDemands({ commit }) {
        const response = await api.demands.list();
        commit("setDemands", response.data);
    },
    async loadDemandsGrid({ commit }) {
        const responseGrid = await api.demands.getForGrid();
        commit("setDemandsGrid", responseGrid.data.output);
    },
    async loadEvaluatedDemandsGrid({ commit }, officeId) {
        const responseGrid = await api.demands.getEvaluatedForGrid(officeId);
        commit("setEvaluatedDemandsGrid", responseGrid.data.output);
    },
    async loadDemandComments({ commit }, demandId) {
        const response = await api.demands.getDemandComments(demandId);
        commit("setDemandComments", response.data.output);
    },
    async saveDemand({ dispatch }, form) {
        const response = await api.demands.save(form);
        await dispatch("fetchDemand", response.data.id);

        dispatch(
            "showSuccess",
            i18n.t("message.success.demandSaved", { code: response.data.code })
        );
    },
    async saveOrUpdateDemand({ dispatch }, form) {
        if (utilities.isEmptyOrZero(form.id)) {
            await dispatch("saveDemand", form);
        } else {
            await dispatch("updateDemand", form);
        }
    },
    async updateDemand({ dispatch }, form) {
        await api.demands.update(form.id, form);
        await dispatch("fetchDemand", form.id);

        dispatch(
            "showSuccess",
            i18n.t("message.success.demandSaved", { code: form.code })
        );
    },

    // Actions
    async findDemandByCode({ commit, dispatch, state }, form) {
        if (utilities.isEmpty(form.code) || state.demand.code === form.code) {
            return;
        }

        const response = await api.demands.findByCode(form);
        if (response.data) {
            commit("setDemand", response.data);
            dispatch("syncDemand", response.data);
        }
    },
    async approveDemand({ dispatch }, { id, comments, files, code }) {
        await api.demands.approve(id, { comments, files });
        await dispatch("fetchDemand", id);

        dispatch(
            "showSuccess",
            i18n.t("message.success.demandApproved", { code })
        );
    },
    async rejectDemand({ dispatch }, { id, comments, files, code }) {
        await api.demands.reject(id, { comments, files });
        await dispatch("fetchDemand", id);

        dispatch(
            "showSuccess",
            i18n.t("message.success.demandRejected", { code })
        );
    },
    async cancelDemand({ dispatch }, { id, comments, files, code }) {
        await api.demands.cancel(id, { comments, files });
        await dispatch("fetchDemand", id);

        dispatch(
            "showSuccess",
            i18n.t("message.success.demandCanceled", { code })
        );
    },
    async requestDemand({ dispatch }, { id, code }) {
        await api.demands.request(id);
        await dispatch("fetchDemand", id);

        dispatch(
            "showSuccess",
            i18n.t("message.success.demandRequested", { code })
        );
    },
    async planDemand({ dispatch }, form) {
        await api.demands.plan(form.id, form);
        await dispatch("fetchDemand", form.id);

        dispatch(
            "showSuccess",
            i18n.t("message.success.demandRequested", { code: form.code })
        );
    },
    async deliverDemand({ dispatch }, form) {
        await api.demands.deliver(form.id, form);
        await dispatch("fetchDemand", form.id);

        dispatch(
            "showSuccess",
            i18n.t("message.success.demandRequested", { code: form.code })
        );
    },

    // Support
    resetDemand({ commit }) {
        const demand = Demand.build(this.getters.profile.person);
        commit("setDemand", demand);
    },
    syncDemand({ commit }, demand) {
        const demands = this.getters.demandsDictionary;

        if (utilities.isEmpty(demands[demand.id])) {
            commit("appendDemand", demand);
        } else {
            commit("updateDemand", demand);
        }
    },
    async findDemandByRequest({ commit, dispatch, state }, form) {
        const response = await api.demands.findByRequest(form);
        return response;
    },
    async findDemandByCreator({ commit, dispatch, state }, form) {
        const response = await api.demands.findByCreator(form);
        return response;
    },
    async findDemandByOwner({ commit, dispatch, state }, form) {
        const response = await api.demands.findByOwner(form);
        return response;
    },
    async searchByFilter({ commit, dispatch, state }, filter) {
        const response = await api.demands.searchByFilter(filter);
        return response;
    }
};

const getters = {
    demand(state) {
        return state.demand;
    },
    demands(state) {
        return state.demands || [];
    },
    demandsGrid(state) {
        return state.demandsGrid || [];
    },
    demandComments(state) {
        return state.demandComments;
    },
    evaluatedDemandsGrid(state) {
        return state.evaluatedDemandsGrid || [];
    },
    demandsValueText(_, getters) {
        return getters.demands.map(({ id, code, title }) => ({
            value: id,
            text: `${code} - ${title}`
        }));
    },
    relatedDemandsValueText(_, getters) {
        const d = getters.demand;
        return getters.demands
            .filter(dm => dm.id !== d.id)
            .map(({ id, code, title }) => ({
                value: id,
                text: `${code} - ${title}`
            }));
    },
    demandsNotCanceledOrClosedValueText(_, getters) {
        return getters.demands
            .filter(dm => dm.status.id !== 8 && dm.status.id !== 9)
            .map(({ id, code, title }) => ({
                value: id,
                text: `${code} - ${title}`
            }));
    },
    demandsDictionary(_, getters) {
        return getters.demands.reduce((map, obj) => {
            map[obj.id] = obj;
            return map;
        }, {});
    },
    demandsDictionaryByCode(_, getters) {
        return getters.demands.reduce((map, obj) => {
            map[obj.code] = obj;
            return map;
        }, {});
    },
    isExternalSupplier(_, getters) {
        return state.demand.system.isExternalSupplier;
    }
};

export default {
    state,
    mutations,
    actions,
    getters
};


// WEBPACK FOOTER //
// ./src/store/modules/demands.js